import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { ApiError, apiGetMeta } from "@/lib/api";
import { onRefresh } from "@/lib/refresh";
import { reportLoad } from "@/lib/staleness";

type State<T> = {
  data: T | null;
  error: string | null;
  loading: boolean;
};

// useApi is the whole data layer: fetch on mount (and whenever the path
// changes), expose a reload for after a mutation, and send the user to /login
// the moment the server says the session is gone. Pages used to be server
// components, so there is no client cache to keep coherent — a reload after a
// write is both correct and cheap.
export function useApi<T>(path: string): State<T> & { reload: () => void } {
  const navigate = useNavigate();
  const [state, setState] = useState<State<T>>({
    data: null,
    error: null,
    loading: true,
  });
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    let active = true;

    setState((prev) => ({ ...prev, loading: true, error: null }));

    apiGetMeta<T>(path, controller.signal)
      .then(({ data, cachedAt }) => {
        if (!active) return;
        // Tell the app whether this screen is live or came out of the cache.
        reportLoad(cachedAt);
        setState({ data, error: null, loading: false });
      })
      .catch((err: unknown) => {
        if (!active || controller.signal.aborted) return;
        if (err instanceof ApiError && err.isUnauthorized) {
          navigate("/login", { replace: true });
          return;
        }
        // Nothing rendered, so there is no stale data to warn about — the
        // error state below says everything the user needs.
        reportLoad(null);
        setState({
          data: null,
          error: err instanceof Error ? err.message : "Something went wrong",
          loading: false,
        });
      });

    return () => {
      active = false;
      controller.abort();
    };
  }, [path, nonce, navigate]);

  const reload = useCallback(() => setNonce((n) => n + 1), []);

  // A write anywhere in the app (offline flush, CSV import) refetches every
  // mounted view, which is what router.refresh() used to do.
  useEffect(() => onRefresh(reload), [reload]);

  return { ...state, reload };
}
